import React, { FunctionComponent, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import FirebaseService from "../../actions/firebase/FirebaseActions";
import { BackButton } from "../common/button/Buttons";
import { ContentLayout } from "../common/Layout";
import { useAuthentication } from "../../hooks/useAuthentication";
import RegistrationEditor, { REGISTRATION_FORM_ID } from "./RegistrationEditor";
import { useFormSchema } from "../../hooks/useFormSchema";
import { trackFormEvent, trackRegistrationEvent } from "../../helper/analytics";
import { trackPlausibleGoal, PlausibleGoals } from "../../helper/plausibleAnalytics";
import { useUserIdParam } from "../../hooks/useUserIdParam";

interface Props {
  isAuthenticated: boolean;
}

const EMPTY_FORM_DATA = {};

const CreateRegistrationEditor: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const userId = useUserIdParam();
  const [formSchema] = useFormSchema(userId);
  const navigate = useNavigate();
  const [isSaving, setSaving] = useState(false);
  useAuthentication(isAuthenticated);

  const saveRegistration = async (formData: unknown): Promise<void> => {
    trackFormEvent("completed", "create-registration-form");
    setSaving(true);

    try {
      const registrationId = await FirebaseService.createRegistration(userId, formData);
      trackRegistrationEvent("created", {
        registration_id: registrationId,
        form_data_keys: Object.keys(formData as Record<string, any>),
        timestamp: new Date().toISOString()
      });
      trackPlausibleGoal(PlausibleGoals.REGISTRATION_CREATED);

      toast.success("Die Anmeldung wurde erstellt");
      navigate(`/user/${userId}/registrations`);
    } catch (e) {
      console.log(e);
      trackRegistrationEvent("create_failed", {
        error: e instanceof Error ? e.message : "Unknown error",
        timestamp: new Date().toISOString()
      });
      trackFormEvent("error", "create-registration-form", {
        error: e instanceof Error ? e.message : "Save failed"
      });
      toast.error(
        "Es ist ein Fehler beim Speichern der Daten aufgetreten. Bitte laden Sie die Applikation erneut und versuchen Sie es noch einmal."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!formSchema) return <p>Daten werden geladen</p>;
  else {
    return (
      <ContentLayout
        title={"Anmeldung erstellen"}
        siteTitle={"Spielgruppe Online - Anmeldung erstellen"}
        maxWidth={860}>
        <div className="editor-toolbar">
          <BackButton to={`/user/${userId}/registrations`} />
          <button
            type="submit"
            form={REGISTRATION_FORM_ID}
            disabled={isSaving}
            className="btn btn-primary editor-toolbar__save">
            Speichern
          </button>
        </div>
        <section className="panel">
          <div className="panel__body">
            <RegistrationEditor
              formSchema={formSchema}
              formData={EMPTY_FORM_DATA}
              isSaving={isSaving}
              onSubmit={saveRegistration}
            />
          </div>
        </section>
      </ContentLayout>
    );
  }
};

export default CreateRegistrationEditor;
